import { toNum } from "@/lib/format";

/**
 * Presentation rules for the CBS-1 apparatus status page (RES-127).
 *
 * The firmware contract is enforced server-side (services/api .../apparatus.py):
 * `value` is null whenever `valid` is false, `heartbeat_stale` is computed
 * against the 60s wifi_status window, and battery/power/health never reach the
 * wire at all. Everything here is display-only and must not re-derive any of it.
 */

/** Mirrors HEARTBEAT_WINDOW_SECONDS in the API. Display-only: the server owns
 * the stale decision, this is for the "no heartbeat in 60s" wording. */
export const HEARTBEAT_WINDOW_SECONDS = 60;

const METRIC_LABELS: Record<string, string> = {
  co: "CO",
  hcn: "HCN",
  voc: "VOC",
  o2: "O₂",
  temperature: "Sensor temperature",
  humidity: "Sensor humidity",
};

export function metricLabel(metric: string): string {
  return METRIC_LABELS[metric] ?? metric;
}

// The firmware's invalid-reading reasons. A metric with valid=false carries one
// of these instead of a number; an unrecognised or blank code still has to say
// something, because a silent blank cell reads as "nothing wrong here".
const REASON_LABELS: Record<string, string> = {
  sensor_not_available: "Sensor not available",
  afe_not_detected: "AFE not detected",
  adc_near_rail_low: "ADC near rail (low)",
  adc_near_rail_high: "ADC near rail (high)",
  out_of_range: "Out of range",
};

export function reasonLabel(reason: string | null | undefined): string {
  if (!reason) return "No reading — reason not reported";
  return REASON_LABELS[reason] ?? reason;
}

/**
 * O2 is not trustworthy in v1: the channel reads INVERTED and the LMP91000
 * register bit-field map is wrong. Suppressing the row entirely would hide a
 * sensor that is genuinely reporting, so it renders labelled instead.
 */
export function isUnreliableMetric(metric: string): boolean {
  return metric === "o2";
}

export const O2_UNRELIABLE_NOTE =
  "O₂ reads inverted and its register map is wrong (FIR-9 signal chain, FIR-12 calibration). Do not read this value as an O₂ level.";

/**
 * Temperature and humidity are the sensor board's OWN readings, used internally
 * to compensate the electrochemical gas cells across temperature. They are not
 * cab-air/ambient measurements (RES-161, confirmed post-pilot 2026-07-28).
 *
 * The distinction matters because the page is framed as "what a parked truck is
 * breathing": a bare "Temperature" next to CO and HCN reads as cab air. The
 * number is right; the implied meaning is wrong. Same honesty class as the
 * report-provenance and VOC-divisor fixes.
 *
 * These are also currently the only valid=true channels the bench unit reports,
 * which makes them the most visible numbers on the page.
 */
export function isCompensationMetric(metric: string): boolean {
  return metric === "temperature" || metric === "humidity";
}

export const COMPENSATION_NOTE =
  "Internal board reading, used to compensate the gas cells across temperature — not a cab-air measurement.";

export const ADVISORY_BANNER_TITLE = "Uncalibrated — advisory only";

export const ADVISORY_BANNER_BODY =
  "These values are uncalibrated: boot code overwrites the NVS calibration on every reboot (FIR-12). The gas channels describe cab air for engineering purposes only and must never be read as a firefighter's exposure. Temperature and humidity are the sensor board's own compensation readings, not cab conditions.";

/**
 * Format a metric value for display. The API serves numerics as JSON **strings**
 * (Pydantic Decimal), so this goes through `toNum` — reading `value` as a number
 * silently drops every row, which is the bug this page was warned about.
 *
 * Returns null when there is nothing safe to show, so callers render the reason.
 */
export function formatMetricValue(
  value: string | number | null | undefined,
  unit: string | null | undefined,
): string | null {
  const n = toNum(value);
  if (n === null) return null;
  const abs = Math.abs(n);
  const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  const rendered = n.toFixed(decimals);
  // The unit travels with the message and is never assumed from the metric key.
  return unit ? `${rendered} ${unit}` : rendered;
}

/**
 * Relative age of the wifi_status heartbeat, e.g. "12s ago".
 *
 * Deliberately never returns a presence word. There is no MQTT Last-Will on this
 * firmware, so silence is only ever "we have not heard from it", never "offline"
 * — and certainly never "online".
 */
export function formatHeartbeatAge(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  if (!iso) return "Never heard from";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "Never heard from";

  const seconds = Math.round((now - then) / 1000);
  if (seconds < 0) return "Just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}
